<script setup lang="ts">
import { computed, ref } from "vue";
import { useTranslations } from "@/composables/useTranslations.ts";

interface GeoInfo {
    city: string | null;
    region: string | null;
    country: string | null;
    org: string | null;
}

const props = defineProps<{
    geo: GeoInfo | null;
    direction?: "up" | "down";
}>();

const { t } = useTranslations();
const dir = computed(() => props.direction ?? "up");

const flagRef = ref<HTMLElement | null>(null);
const visible = ref(false);
const tooltipPos = ref({ top: "", bottom: "", left: "" });
const toast = ref("");

const countryCode = computed(() => props.geo?.country?.toLowerCase() ?? null);

const countryName = computed((): string | null => {
    const code = props.geo?.country ?? null;

    if (code === null) {
        return null;
    }

    try {
        return (
            new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code
        );
    } catch {
        return code;
    }
});

const locationLine = computed((): string | null => {
    const parts = [props.geo?.city, props.geo?.region].filter(
        (v): v is string => v !== null && v !== undefined && v !== "",
    );

    return parts.length > 0 ? parts.join(", ") : null;
});

const copyText = computed((): string =>
    [countryName.value, locationLine.value, props.geo?.org]
        .filter((v): v is string => v !== null && v !== undefined && v !== "")
        .join(" · "),
);

function showTooltip(): void {
    if (!flagRef.value) {
        return;
    }

    const rect = flagRef.value.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;

    if (dir.value === "up") {
        tooltipPos.value = {
            bottom: `${window.innerHeight - rect.top + 8}px`,
            top: "",
            left: `${cx}px`,
        };
    } else {
        tooltipPos.value = {
            top: `${rect.bottom + 8}px`,
            bottom: "",
            left: `${cx}px`,
        };
    }

    visible.value = true;
}

function hideTooltip(): void {
    visible.value = false;
}

async function writeToClipboard(text: string): Promise<void> {
    if (navigator.clipboard !== undefined && window.isSecureContext === true) {
        try {
            await navigator.clipboard.writeText(text);
            return;
        } catch (error) {
            console.error(error);
        }
    }

    const ta = document.createElement("textarea");
    const activeEl = document.activeElement as HTMLElement | null;

    ta.value = text;
    ta.readOnly = true;
    ta.setAttribute("aria-hidden", "true");
    ta.style.cssText = "position:fixed;left:-9999px;top:-9999px;opacity:0;";

    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, ta.value.length);

    const ok = document.execCommand("copy");

    document.body.removeChild(ta);
    activeEl?.focus();

    if (ok === false) {
        throw new Error("Clipboard copy failed.");
    }
}

async function copyGeo(): Promise<void> {
    if (!copyText.value) {
        return;
    }

    try {
        await writeToClipboard(copyText.value);
        toast.value = t("vue.ip_geo_copied");
    } catch (error) {
        console.error(error);
    } finally {
        setTimeout(() => {
            toast.value = "";
        }, 1200);
    }
}
</script>

<template>
    <span v-if="countryCode" class="inline-flex">
        <span
            ref="flagRef"
            :class="`fi fi-${countryCode}`"
            class="ml-1.5 cursor-pointer rounded-[1px]"
            style="font-size: 0.75em; line-height: 1"
            :aria-label="countryName ?? countryCode"
            :title="t('vue.click_to_copy_ip_geo')"
            role="img"
            @mouseenter="showTooltip"
            @mouseleave="hideTooltip"
            @click.stop="copyGeo"
        ></span>

        <Teleport to="body">
            <Transition name="ip-tooltip">
                <div
                    v-if="visible"
                    class="pointer-events-none fixed z-[9999] min-w-[130px] max-w-[190px] -translate-x-1/2 rounded-md bg-gray-900 px-2.5 py-1.5 text-left text-xs font-medium leading-snug text-white shadow-[0_4px_16px_rgba(0,0,0,0.18)]"
                    :style="tooltipPos"
                >
                    <!-- arrow up (tooltip below flag) -->
                    <span
                        v-if="dir === 'down'"
                        class="absolute left-1/2 top-0 h-0 w-0 -translate-x-1/2 -translate-y-full border-x-[6px] border-x-transparent border-b-[6px] border-b-gray-900"
                    ></span>
                    <!-- arrow down (tooltip above flag) -->
                    <span
                        v-else
                        class="absolute bottom-0 left-1/2 h-0 w-0 -translate-x-1/2 translate-y-full border-x-[6px] border-x-transparent border-t-[6px] border-t-gray-900"
                    ></span>

                    <span class="block font-semibold">{{
                        countryName ?? countryCode
                    }}</span>
                    <span
                        v-if="locationLine"
                        class="mt-0.5 block text-[11px] text-gray-300"
                        >{{ locationLine }}</span
                    >
                    <span
                        v-if="geo?.org"
                        class="mt-0.5 block text-[11px] text-gray-400"
                        >{{ geo.org }}</span
                    >
                    <span
                        class="mt-1.5 block border-t border-gray-700 pt-1 text-[10px] text-gray-400"
                    >
                        {{ t("vue.click_to_copy_ip_geo") }}
                    </span>
                </div>
            </Transition>

            <div
                v-if="toast"
                class="fixed bottom-[18px] right-[18px] z-[9999] rounded-[8px] bg-[#161E2E] px-[10px] py-[8px] text-[12px] text-white"
            >
                {{ toast }}
            </div>
        </Teleport>
    </span>
</template>

<style scoped>
.ip-tooltip-enter-active,
.ip-tooltip-leave-active {
    transition:
        opacity 0.15s ease,
        translate 0.15s ease;
}

.ip-tooltip-enter-from,
.ip-tooltip-leave-to {
    opacity: 0;
    translate: 0 4px;
}
</style>
